import React, { useMemo, useRef, useState } from "react";
import { SearchOutlined, HomeOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Space, Table } from "antd";
import type { InputRef } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import "./data_vehicle_master.css";
import useSWR from "swr";
import axios from "axios";
import { useNavigate } from "react-router-dom";

interface DataType {
    key: React.Key;
    chassisNo: string;
    engineNo: string;
    noPol: string;
    stnkName: string;
    vehicleDescription: string;
    lastKM: number;
    status: string;
}

interface VehicleDataFromAPI {
    id: number;
    vehicle_chassis_no: string;
    vehicle_engine_no: string;
    no_polisi: string;
    name: string;
    brand: string;
    model: string;
    last_km: number;
    status: string;
}

type DataIndex = keyof DataType;

const DataVehicleMaster: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();

    const fetcher = (url: string) => axios.get(url).then(res => res.data);
    const { data: vehicles, isLoading } = useSWR<VehicleDataFromAPI[]>(`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/vehicles`, fetcher);

    const tableData = useMemo(() => {
        return vehicles?.map(v => ({
            key: v.id,
            chassisNo: v.vehicle_chassis_no,
            engineNo: v.vehicle_engine_no,
            noPol: v.no_polisi,
            stnkName: v.name,
            vehicleDescription: `${v.brand || ''} ${v.model || ''}`.trim(),
            lastKM: v.last_km,
            status: v.status
        }));
    }, [vehicles]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex] != null
                ? record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase())
                : false,
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) => {
            if (searchedColumn === dataIndex && searchText) {
                const textStr = text != null ? text.toString() : '';
                const regex = new RegExp(`(${searchText.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
                const parts = textStr.split(regex);
                return (
                    <span>
                        {parts.map((part: string, i: number) =>
                            regex.test(part) ? (
                                <mark key={i} style={{ backgroundColor: '#ffc069', padding: 0 }}>
                                    {part}
                                </mark>
                            ) : (
                                part
                            )
                        )}
                    </span>
                );
            }
            return text;
        },
    });

    const columns: ColumnsType<DataType> = [
        {
            title: 'Chassis No',
            dataIndex: 'chassisNo',
            key: 'chassisNo',
            sorter: (a, b) => (a.chassisNo || '').localeCompare(b.chassisNo || ''),
            ...getColumnSearchProps('chassisNo'),
        },
        {
            title: 'Engine No',
            dataIndex: 'engineNo',
            key: 'engineNo',
            sorter: (a, b) => (a.engineNo || '').localeCompare(b.engineNo || ''),
            ...getColumnSearchProps('engineNo'),
        },
        {
            title: 'No. Pol',
            dataIndex: 'noPol',
            key: 'noPol',
            sorter: (a, b) => (a.noPol || '').localeCompare(b.noPol || ''),
            ...getColumnSearchProps('noPol'),
        },
        {
            title: 'STNK Name',
            dataIndex: 'stnkName',
            key: 'stnkName',
            sorter: (a, b) => (a.stnkName || '').localeCompare(b.stnkName || ''),
            ...getColumnSearchProps('stnkName'),
        },
        {
            title: 'Vehicle Description',
            dataIndex: 'vehicleDescription',
            key: 'vehicleDescription',
            sorter: (a, b) => (a.vehicleDescription || '').localeCompare(b.vehicleDescription || ''),
            ...getColumnSearchProps('vehicleDescription'),
        },
        {
            title: 'Last KM',
            dataIndex: 'lastKM',
            key: 'lastKM',
            sorter: (a, b) => (a.lastKM || 0) - (b.lastKM || 0),
            ...getColumnSearchProps('lastKM'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
            filters: [
                { text: 'Active', value: 'Active' },
                { text: 'Deactive', value: 'Deactive' },
            ],
            onFilter: (value, record) => record.status === value,
        },
    ];

    return (
        <div id="iiok" style={transformStyle}>
            <div className="gjs-grid-row" id="izg8">
                <div className="gjs-grid-column" id="icfm">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data Vehicle Master</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/vehicle_master",
                                title: "Vehicle Master",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="iwbx2">
                <div className="gjs-grid-column" id="ipk6x">
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        loading={isLoading}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                navigate(`/dashboard/vehicle_master`, { state: { vehicleId: record.key } });
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataVehicleMaster;